import {
  getStoredAccessToken,
  getStoredRefreshToken,
  updateStoredAccessToken,
} from "./authStorage";
import { resolveGraphqlEndpoint } from "./resolveGraphqlEndpoint";

const GQL_ENDPOINT = resolveGraphqlEndpoint();
const DEFAULT_EXPIRY_BUFFER_MS = 2 * 60 * 1000;
const DEFAULT_FALLBACK_INTERVAL_MS = 10 * 60 * 1000;

const IAM_REFRESH_TOKEN_MUTATION = `
  mutation IamRefreshToken($refreshToken: String!) {
    iamRefreshToken(input: { refreshToken: $refreshToken }) {
      accessToken
    }
  }
`;

export const SESSION_REFRESHED_EVENT = "smartstore:session-refreshed";

export type SessionRefreshedEventDetail = {
  accessToken: string;
};

export type SessionRefreshResult =
  | { ok: true; accessToken: string; skipped?: false }
  | { ok: true; skipped: true }
  | {
      ok: false;
      reason:
        | "missing_refresh_token"
        | "invalid_refresh_token"
        | "network"
        | "invalid_response";
      message?: string;
    };

type GraphQLErrorPayload = {
  message?: string;
  extensions?: {
    code?: unknown;
  };
};

type RefreshTokenResponse = {
  data?: {
    iamRefreshToken?: {
      accessToken?: unknown;
    } | null;
  } | null;
  errors?: GraphQLErrorPayload[];
};

let refreshInFlight: Promise<SessionRefreshResult> | null = null;
let lastSuccessfulRefreshAt = 0;

function decodeBase64Url(value: string): string {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  return atob(padded);
}

export function getJwtExpiryMs(token: string | null): number | null {
  if (!token) return null;

  const [, payload] = token.split(".");
  if (!payload) return null;

  try {
    const parsed = JSON.parse(decodeBase64Url(payload)) as { exp?: unknown };
    return typeof parsed.exp === "number" ? parsed.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isRefreshAuthFailure(
  status: number,
  response: RefreshTokenResponse | null,
): boolean {
  if (status === 401 || status === 403) return true;

  const code = response?.errors?.[0]?.extensions?.code;
  return code === "UNAUTHENTICATED" || code === "TOKEN_EXPIRED";
}

function dispatchSessionRefreshed(accessToken: string): void {
  window.dispatchEvent(
    new CustomEvent<SessionRefreshedEventDetail>(SESSION_REFRESHED_EVENT, {
      detail: { accessToken },
    }),
  );
}

async function readRefreshResponse(
  response: Response,
): Promise<RefreshTokenResponse | null> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as RefreshTokenResponse;
  } catch {
    return null;
  }
}

async function refreshSessionOnce(): Promise<SessionRefreshResult> {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    return { ok: false, reason: "missing_refresh_token" };
  }

  try {
    const response = await fetch(GQL_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: IAM_REFRESH_TOKEN_MUTATION,
        variables: { refreshToken },
      }),
    });
    const payload = await readRefreshResponse(response);

    if (!response.ok || payload?.errors?.length) {
      return {
        ok: false,
        reason: isRefreshAuthFailure(response.status, payload)
          ? "invalid_refresh_token"
          : "network",
        message:
          payload?.errors?.[0]?.message ||
          `Session refresh failed with HTTP ${response.status}`,
      };
    }

    const accessToken = payload?.data?.iamRefreshToken?.accessToken;
    if (typeof accessToken !== "string" || !accessToken) {
      return {
        ok: false,
        reason: "invalid_response",
        message: "Session refresh response did not include an access token.",
      };
    }

    updateStoredAccessToken(accessToken);
    lastSuccessfulRefreshAt = Date.now();
    dispatchSessionRefreshed(accessToken);

    return { ok: true, accessToken };
  } catch (error) {
    return {
      ok: false,
      reason: "network",
      message: error instanceof Error ? error.message : "Network error",
    };
  }
}

export function refreshSession(): Promise<SessionRefreshResult> {
  if (!refreshInFlight) {
    refreshInFlight = refreshSessionOnce().finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
}

export async function refreshSessionIfNeeded(
  options: {
    force?: boolean;
    expiryBufferMs?: number;
    fallbackIntervalMs?: number;
  } = {},
): Promise<SessionRefreshResult> {
  const {
    force = false,
    expiryBufferMs = DEFAULT_EXPIRY_BUFFER_MS,
    fallbackIntervalMs = DEFAULT_FALLBACK_INTERVAL_MS,
  } = options;

  if (!force) {
    const accessToken = getStoredAccessToken();
    const expiryMs = getJwtExpiryMs(accessToken);
    const now = Date.now();

    if (expiryMs && expiryMs > now + expiryBufferMs) {
      return { ok: true, skipped: true };
    }

    if (!expiryMs && now - lastSuccessfulRefreshAt < fallbackIntervalMs) {
      return { ok: true, skipped: true };
    }
  }

  return refreshSession();
}

export function shouldRedirectAfterRefreshFailure(
  result: SessionRefreshResult,
): boolean {
  return (
    result.ok === false &&
    (result.reason === "missing_refresh_token" ||
      result.reason === "invalid_refresh_token" ||
      result.reason === "invalid_response")
  );
}
